import { useEffect, useMemo, useState } from 'react';
import type { Contractor, Product, ProjectProduct } from '@bpd/shared';
import { computeProductLineTotal, formatCurrency } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';
import { CurrencyInput } from '../CurrencyInput.tsx';
import { loadTaskOptions, taskLabel, type TaskOption } from './taskOptions.ts';
import { ContractorPicker } from './ContractorPicker.tsx';
import type { InvoicePrefill, InvoiceActivity } from './ProjectInvoicesSection.tsx';

interface AddLineFormProps {
  catalog: Product[];
  taskOptions: TaskOption[];
  contractors: Contractor[];
  onContractorCreated: (contractor: Contractor) => void;
  onAdded: (line: ProjectProduct) => void;
  onCancel: () => void;
  onCatalogProductCreated: (product: Product) => void;
  projectId: string;
}

function AddLineForm({
  catalog,
  taskOptions,
  contractors,
  onContractorCreated,
  onAdded,
  onCancel,
  onCatalogProductCreated,
  projectId,
}: AddLineFormProps) {
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [taxAmount, setTaxAmount] = useState('0');
  const [delivery, setDelivery] = useState('0');
  const [taskItemId, setTaskItemId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showNewProduct, setShowNewProduct] = useState(false);
  const [newItem, setNewItem] = useState('');
  const [newSku, setNewSku] = useState('');
  const [newUnitPrice, setNewUnitPrice] = useState('');
  const [newContractorId, setNewContractorId] = useState('');
  const [savingProduct, setSavingProduct] = useState(false);

  async function handleAddCatalogProduct() {
    const price = Number(newUnitPrice);
    if (!newItem.trim() || !Number.isFinite(price) || price < 0) return;
    setSavingProduct(true);
    try {
      const created = await projectsApi.createProduct({
        item: newItem.trim(),
        ...(newSku ? { sku: newSku } : {}),
        unitPrice: price,
        ...(newContractorId ? { contractorId: newContractorId } : {}),
      });
      onCatalogProductCreated(created);
      setProductId(created.id);
      setShowNewProduct(false);
      setNewItem('');
      setNewSku('');
      setNewUnitPrice('');
      setNewContractorId('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add product');
    } finally {
      setSavingProduct(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const product = catalog.find((p) => p.id === productId);
    const qty = Number(quantity);
    const tax = Number(taxAmount);
    const delAmt = Number(delivery);
    if (!product || !Number.isFinite(qty) || qty <= 0 || !Number.isFinite(tax) || !Number.isFinite(delAmt)) {
      setError('Pick a product and a positive quantity');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await projectsApi.createProjectProduct({
        projectId,
        productId: product.id,
        item: product.item,
        ...(product.description ? { description: product.description } : {}),
        ...(product.sku ? { sku: product.sku } : {}),
        unitPrice: product.unitPrice,
        quantity: qty,
        taxAmount: tax,
        delivery: delAmt,
        ...(product.contractorId ? { contractorId: product.contractorId } : {}),
        ...(taskItemId ? { taskItemId } : {}),
      });
      onAdded(created);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="mt-3 space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
    >
      {!showNewProduct ? (
        <div className="flex gap-2">
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="">— Pick a product —</option>
            {catalog.map((p) => (
              <option key={p.id} value={p.id}>
                {p.item}
                {p.sku ? ` (${p.sku})` : ''} — {formatCurrency(p.unitPrice)}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowNewProduct(true)}
            className="shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            + New
          </button>
        </div>
      ) : (
        <div className="space-y-2 rounded-md border border-gray-200 p-3">
          <input
            type="text"
            placeholder="Item *"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="SKU"
              value={newSku}
              onChange={(e) => setNewSku(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
            <CurrencyInput value={newUnitPrice} onChange={setNewUnitPrice} placeholder="Unit price *" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500">Vendor (optional)</label>
            <div className="mt-0.5">
              <ContractorPicker
                contractors={contractors}
                value={newContractorId}
                onChange={setNewContractorId}
                onContractorCreated={onContractorCreated}
                label="vendor"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void handleAddCatalogProduct()}
              disabled={savingProduct || !newItem.trim()}
              className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
            >
              {savingProduct ? 'Adding…' : 'Add to catalog'}
            </button>
            <button type="button" onClick={() => setShowNewProduct(false)} className="text-sm text-gray-500 hover:underline">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-500">Task / subtask (optional)</label>
        <select
          value={taskItemId}
          onChange={(e) => setTaskItemId(e.target.value)}
          className="mt-0.5 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        >
          <option value="">— Project-level (no specific task) —</option>
          {taskOptions.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-500">Quantity</label>
          <input
            type="number"
            min="1"
            step="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="mt-0.5 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500">Tax</label>
          <CurrencyInput value={taxAmount} onChange={setTaxAmount} className="mt-0.5" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500">Delivery</label>
          <CurrencyInput value={delivery} onChange={setDelivery} className="mt-0.5" />
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-phoenix-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
        >
          {saving ? 'Adding…' : 'Add to project'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:underline">
          Cancel
        </button>
      </div>
    </form>
  );
}

interface EditLineFormProps {
  line: ProjectProduct;
  taskOptions: TaskOption[];
  contractors: Contractor[];
  onContractorCreated: (contractor: Contractor) => void;
  projectId: string;
  onSaved: (line: ProjectProduct) => void;
  onCancel: () => void;
}

function EditLineForm({ line, taskOptions, contractors, onContractorCreated, projectId, onSaved, onCancel }: EditLineFormProps) {
  const [item, setItem] = useState(line.item);
  const [description, setDescription] = useState(line.description ?? '');
  const [sku, setSku] = useState(line.sku ?? '');
  const [unitPrice, setUnitPrice] = useState(String(line.unitPrice));
  const [quantity, setQuantity] = useState(String(line.quantity));
  const [taxAmount, setTaxAmount] = useState(String(line.taxAmount));
  const [delivery, setDelivery] = useState(String(line.delivery));
  const [contractorId, setContractorId] = useState(line.contractorId ?? '');
  const [taskItemId, setTaskItemId] = useState(line.taskItemId ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    const price = Number(unitPrice);
    const qty = Number(quantity);
    const tax = Number(taxAmount);
    const delAmt = Number(delivery);
    if (!item.trim() || !Number.isFinite(price) || price < 0 || !Number.isFinite(qty) || qty <= 0) {
      setError('Item and a positive quantity/price are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await projectsApi.updateProjectProduct(line.id, projectId, {
        item: item.trim(),
        ...(description ? { description } : {}),
        ...(sku ? { sku } : {}),
        unitPrice: price,
        quantity: qty,
        taxAmount: tax,
        delivery: delAmt,
        contractorId: contractorId || null,
        taskItemId: taskItemId || null,
      });
      onSaved(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-phoenix-blue-200 bg-phoenix-blue-50/40 p-3">
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          placeholder="Item"
          value={item}
          onChange={(e) => setItem(e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-1 text-sm"
        />
        <input
          type="text"
          placeholder="SKU"
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-1 text-sm"
        />
      </div>
      <input
        type="text"
        placeholder="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
      />
      <div className="grid grid-cols-4 gap-2">
        <CurrencyInput value={unitPrice} onChange={setUnitPrice} placeholder="Unit price" />
        <input
          type="number"
          min="1"
          step="1"
          placeholder="Qty"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-1 text-sm"
        />
        <CurrencyInput value={taxAmount} onChange={setTaxAmount} placeholder="Tax" />
        <CurrencyInput value={delivery} onChange={setDelivery} placeholder="Delivery" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500">Vendor</label>
        <div className="mt-0.5">
          <ContractorPicker
            contractors={contractors}
            value={contractorId}
            onChange={setContractorId}
            onContractorCreated={onContractorCreated}
            label="vendor"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500">Task / subtask</label>
        <select
          value={taskItemId}
          onChange={(e) => setTaskItemId(e.target.value)}
          className="mt-0.5 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
        >
          <option value="">— Project-level (no specific task) —</option>
          {taskOptions.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="rounded-md bg-phoenix-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:underline">
          Cancel
        </button>
      </div>
    </div>
  );
}

interface ProjectProductsSectionProps {
  projectId: string;
  contractors: Contractor[];
  onContractorCreated: (contractor: Contractor) => void;
  /** Reports the running total up to the parent (ProjectDetailPage) for the page-level Budget
   * summary — recomputed whenever the line items change. */
  onTotalChange: (total: number) => void;
  /** Reports each task-linked line's total, summed per taskItemId, up to ProjectDetailPage — the
   * Products counterpart to ProjectEstimatesSection's onTaskEstimateTotalsChange, feeding the same
   * pooled per-task "not yet invoiced" figure (computeScopeRemaining). Project-level (no task)
   * lines are omitted since they're covered by the plain grand total minus this sum. */
  onTaskTotalsChange: (totals: Record<string, number>) => void;
  /** Hands a "Convert to Invoice" click up to ProjectDetailPage, which forwards it into
   * ProjectInvoicesSection as a pre-fill for its add-invoice form — same lift-up pattern as
   * ProjectEstimatesSection's onConvertToInvoice. */
  onConvertToInvoice: (prefill: InvoicePrefill) => void;
  /** Every invoice's sourceProjectProductIds, used to mark already-converted lines "Converted to
   * invoice" and keep them out of a new selection — same source ProjectEstimatesSection reads for
   * its own "already converted" check. */
  invoiceActivity: InvoiceActivity[];
}

export function ProjectProductsSection({
  projectId,
  contractors,
  onContractorCreated,
  onTotalChange,
  onTaskTotalsChange,
  onConvertToInvoice,
  invoiceActivity,
}: ProjectProductsSectionProps) {
  const [lines, setLines] = useState<ProjectProduct[]>([]);
  const [catalog, setCatalog] = useState<Product[]>([]);
  const [taskOptions, setTaskOptions] = useState<TaskOption[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const convertedProductIds = useMemo(
    () => new Set(invoiceActivity.flatMap((a) => a.sourceProjectProductIds ?? [])),
    [invoiceActivity],
  );

  // Drops a selected id the moment it stops being selectable (deleted, marked inactive, or
  // converted by an invoice created elsewhere) — otherwise a stale id could ride along into a
  // later conversion's sourceProjectProductIds.
  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set(
        [...prev].filter((id) => lines.some((l) => l.id === id && l.isActive !== false) && !convertedProductIds.has(id)),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [lines, convertedProductIds]);

  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleConvertSelected() {
    const selected = lines.filter((l) => selectedIds.has(l.id));
    if (selected.length === 0) return;
    const billedAmount = selected.reduce((sum, l) => sum + computeProductLineTotal(l).total, 0);
    const taskItemIds = new Set(selected.map((l) => l.taskItemId ?? ''));
    const sharedTaskItemId = taskItemIds.size === 1 ? [...taskItemIds][0] : '';
    const contractorIds = new Set(selected.map((l) => l.contractorId).filter((id): id is string => !!id));
    const vendorName =
      contractorIds.size === 1 ? contractors.find((c) => c.id === [...contractorIds][0])?.name : undefined;
    const title = (vendorName ? `${vendorName} purchase — ` : '') + selected.map((l) => l.item).join(', ');
    onConvertToInvoice({
      title: title.slice(0, 200),
      billedAmount,
      ...(sharedTaskItemId ? { taskItemId: sharedTaskItemId } : {}),
      sourceProjectProductIds: [...selectedIds],
    });
    setSelectedIds(new Set());
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  useEffect(() => {
    void Promise.all([projectsApi.listProjectProducts(projectId), projectsApi.listProducts(), loadTaskOptions(projectId)]).then(
      ([lineData, catalogData, taskOptionData]) => {
        setLines(lineData);
        setCatalog(catalogData);
        setTaskOptions(taskOptionData);
        setLoaded(true);
      },
    );
  }, [projectId]);

  useEffect(() => {
    // isActive has no exclusivity (same convention as Estimate.isActive) — an inactive line (e.g.
    // a duplicate purchase already covered by an Estimate) stays visible below but is dropped from
    // the totals fed up to the page-level Budget, mirroring ProjectEstimatesSection's
    // onEstimatesChange filter.
    const active = lines.filter((l) => l.isActive !== false);
    onTotalChange(active.reduce((sum, l) => sum + computeProductLineTotal(l).total, 0));
    const taskTotals: Record<string, number> = {};
    for (const l of active) {
      if (!l.taskItemId) continue;
      taskTotals[l.taskItemId] = (taskTotals[l.taskItemId] ?? 0) + computeProductLineTotal(l).total;
    }
    onTaskTotalsChange(taskTotals);
  }, [lines, onTotalChange, onTaskTotalsChange]);

  async function handleDelete(id: string) {
    await projectsApi.deleteProjectProduct(id, projectId);
    setLines((prev) => prev.filter((l) => l.id !== id));
    setConfirmDeleteId(null);
  }

  async function handleMarkActive(id: string) {
    const updated = await projectsApi.updateProjectProduct(id, projectId, { isActive: true });
    setLines((prev) => prev.map((l) => (l.id === id ? updated : l)));
  }

  async function handleMarkInactive(id: string) {
    const updated = await projectsApi.updateProjectProduct(id, projectId, { isActive: false });
    setLines((prev) => prev.map((l) => (l.id === id ? updated : l)));
  }

  if (!loaded) return null;

  const total = lines.filter((l) => l.isActive !== false).reduce((sum, l) => sum + computeProductLineTotal(l).total, 0);

  return (
    <section className="mt-10">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl font-semibold text-phoenix-blue-900">Products</h2>
        <div className="flex items-center gap-3">
          {lines.length > 0 && (
            <span className="text-sm text-gray-600">
              Products total <span className="font-semibold text-gray-900">{formatCurrency(total)}</span>
            </span>
          )}
          {selectedIds.size > 0 && (
            <button
              type="button"
              onClick={handleConvertSelected}
              className="rounded-md border border-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-phoenix-blue-600 hover:bg-phoenix-blue-50"
            >
              Convert {selectedIds.size} to invoice
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowAdd((v) => !v)}
            className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700"
          >
            {showAdd ? 'Cancel' : '+ Add product'}
          </button>
        </div>
      </div>

      {showAdd && (
        <AddLineForm
          projectId={projectId}
          catalog={catalog}
          taskOptions={taskOptions}
          contractors={contractors}
          onContractorCreated={onContractorCreated}
          onCatalogProductCreated={(p) => setCatalog((prev) => [...prev, p].sort((a, b) => a.item.localeCompare(b.item)))}
          onAdded={(line) => {
            setLines((prev) => [...prev, line]);
            setShowAdd(false);
          }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {lines.length === 0 ? (
        <p className="mt-3 text-sm text-gray-400">No products added yet.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {lines.map((line) => {
            const { subtotal, total: lineTotal } = computeProductLineTotal(line);
            const isActive = line.isActive !== false;
            const isExpanded = expanded.has(line.id);
            const isEditing = editingId === line.id;
            const isConverted = convertedProductIds.has(line.id);
            const vendorName = line.contractorId ? contractors.find((c) => c.id === line.contractorId)?.name : undefined;
            return (
              <div key={line.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                {isEditing ? (
                  <EditLineForm
                    line={line}
                    taskOptions={taskOptions}
                    contractors={contractors}
                    onContractorCreated={onContractorCreated}
                    projectId={projectId}
                    onSaved={(updated) => {
                      setLines((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
                      setEditingId(null);
                    }}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {isActive && !isConverted && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(line.id)}
                          onChange={() => toggleSelected(line.id)}
                          aria-label={`Select ${line.item} for invoice conversion`}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => toggleExpand(line.id)}
                        className={`flex flex-wrap items-center gap-2 text-left ${isActive ? '' : 'opacity-50'}`}
                      >
                        <span className="text-xs text-phoenix-blue-500">{isExpanded ? '▼' : '▶'}</span>
                        <span className="font-semibold text-gray-900">{line.item}</span>
                        {!isActive && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                            Inactive
                          </span>
                        )}
                        {vendorName && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">{vendorName}</span>
                        )}
                        {line.taskItemId && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                            {taskLabel(taskOptions, line.taskItemId)}
                          </span>
                        )}
                        {isConverted && (
                          <span
                            className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500"
                            title="An invoice has already been converted from this product line."
                          >
                            Converted to invoice
                          </span>
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span>
                        {formatCurrency(line.unitPrice)} × {line.quantity} = {formatCurrency(lineTotal)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setEditingId(line.id)}
                        className="text-gray-500 hover:underline"
                      >
                        Edit
                      </button>
                      {isActive ? (
                        <button
                          type="button"
                          onClick={() => void handleMarkInactive(line.id)}
                          className="text-gray-500 hover:underline"
                        >
                          Mark inactive
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void handleMarkActive(line.id)}
                          className="text-phoenix-blue-600 hover:underline"
                        >
                          Mark active
                        </button>
                      )}
                      {confirmDeleteId !== line.id ? (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(line.id)}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => void handleDelete(line.id)}
                            className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-700"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(null)}
                            className="text-gray-500 hover:underline"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {isExpanded && !isEditing && (
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-gray-100 pt-2 text-sm sm:grid-cols-4">
                    {line.sku && (
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">SKU</dt>
                        <dd className="text-gray-700">{line.sku}</dd>
                      </div>
                    )}
                    {line.description && (
                      <div className="col-span-2">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Description</dt>
                        <dd className="text-gray-700">{line.description}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Sub-total</dt>
                      <dd className="text-gray-700">{formatCurrency(subtotal)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Tax</dt>
                      <dd className="text-gray-700">{formatCurrency(line.taxAmount)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Delivery</dt>
                      <dd className="text-gray-700">{formatCurrency(line.delivery)}</dd>
                    </div>
                  </dl>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
